
import { useState, useCallback, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Report } from "@/types/report";
import { useToast } from "@/hooks/use-toast";

const ITEMS_PER_PAGE = 10;
const STORAGE_KEY = "admin-reports-filters";

interface StoredFilters {
  search?: string;
  statusFilter?: string;
  learningStatusFilter?: string;
  submittedOnTimeFilter?: string;
  hoursDeductedFilter?: string;
  teachingDateFrom?: string | null;
  teachingDateTo?: string | null;
  showOtherSubjectsOnly?: boolean;
}

const loadFilters = (): StoredFilters => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
};

const saveFilters = (filters: StoredFilters) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(filters)); } catch {}
};

export const useAdminReports = () => {
  const stored = loadFilters();

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState(stored.search ?? "");
  const [pendingSearch, setPendingSearch] = useState(stored.search ?? "");
  const [showOtherSubjectsOnly, setShowOtherSubjectsOnly] = useState(stored.showOtherSubjectsOnly ?? false);
  const [sortField, setSortField] = useState("created_at");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  const [statusFilter, setStatusFilter] = useState<string>(stored.statusFilter ?? "ALL");
  const [learningStatusFilter, setLearningStatusFilter] = useState<string>(stored.learningStatusFilter ?? "ALL");
  const [submittedOnTimeFilter, setSubmittedOnTimeFilter] = useState<string>(stored.submittedOnTimeFilter ?? "ALL");
  const [hoursDeductedFilter, setHoursDeductedFilter] = useState<string>(stored.hoursDeductedFilter ?? "ALL");
  const [teachingDateFrom, setTeachingDateFrom] = useState<Date | undefined>(stored.teachingDateFrom ? new Date(stored.teachingDateFrom) : undefined);
  const [teachingDateTo, setTeachingDateTo] = useState<Date | undefined>(stored.teachingDateTo ? new Date(stored.teachingDateTo) : undefined);

  const { toast } = useToast();

  const { data: reports = [], isLoading, refetch, isFetching } = useQuery({
    queryKey: [
      'admin-reports', currentPage, search, showOtherSubjectsOnly, sortField, sortDirection,
      statusFilter, learningStatusFilter, submittedOnTimeFilter, hoursDeductedFilter,
      teachingDateFrom?.toISOString(), teachingDateTo?.toISOString(),
    ],
    queryFn: async () => {
      let supabaseQuery = supabase
        .from('student_development_reports')
        .select('id, created_at, student_nickname, student_full_name, subject, teaching_date, status, start_date, end_date, teaching_time_slot, teacher_display, submitter_email, learning_status, submitted_on_time, rejection_reason, hours_deducted, created_by_admin, teaching_content, other_subject', { count: 'exact' })
        .order(sortField, { ascending: sortDirection === 'asc' });

      if (search.trim()) {
        const searchTerm = search.trim();
        supabaseQuery = supabaseQuery.or(
          [
            `student_nickname.ilike.%${searchTerm}%`,
            `student_full_name.ilike.%${searchTerm}%`,
            `teacher_display.ilike.%${searchTerm}%`,
            `subject.ilike.%${searchTerm}%`,
            `submitter_email.ilike.%${searchTerm}%`,
            `teacher_id.ilike.%${searchTerm}%`
          ].join(',')
        );
      }

      if (showOtherSubjectsOnly) {
        supabaseQuery = supabaseQuery.eq('other_subject', true);
      }

      // Status filter
      if (statusFilter !== 'ALL') {
        supabaseQuery = supabaseQuery.eq('status', statusFilter);
      }

      // Learning status filter
      if (learningStatusFilter === 'NORMAL') {
        supabaseQuery = supabaseQuery.eq('learning_status', 'เข้าเรียน/เข้าสอนปกติ');
      } else if (learningStatusFilter === 'ABSENT') {
        supabaseQuery = supabaseQuery.eq('learning_status', 'น้องไม่เข้าเรียน (ลากะทันหัน)');
      }

      // Submitted on time filter
      if (submittedOnTimeFilter === 'YES') {
        supabaseQuery = supabaseQuery.eq('submitted_on_time', true);
      } else if (submittedOnTimeFilter === 'NO') {
        supabaseQuery = supabaseQuery.eq('submitted_on_time', false);
      }

      // Hours deducted filter
      if (hoursDeductedFilter === 'YES') {
        supabaseQuery = supabaseQuery.eq('hours_deducted', true);
      } else if (hoursDeductedFilter === 'NO') {
        supabaseQuery = supabaseQuery.eq('hours_deducted', false);
      }

      // Teaching date range
      if (teachingDateFrom) {
        const fromStr = teachingDateFrom.toISOString().split('T')[0];
        supabaseQuery = supabaseQuery.gte('teaching_date', fromStr);
      }
      if (teachingDateTo) {
        const toStr = teachingDateTo.toISOString().split('T')[0];
        supabaseQuery = supabaseQuery.lte('teaching_date', toStr);
      }

      const from = (currentPage - 1) * ITEMS_PER_PAGE;
      const to = from + ITEMS_PER_PAGE - 1;
      const { data, error, count } = await supabaseQuery.range(from, to);

      if (error) throw error;
      setTotalPages(Math.max(1, Math.ceil((count || 0) / ITEMS_PER_PAGE)));

      return (Array.isArray(data) ? data.filter(item =>
        typeof item === 'object' &&
        item !== null &&
        'id' in item
      ) : []) as Report[];
    }
  });

  const onSearch = useCallback(() => {
    setCurrentPage(1);
    setSearch(pendingSearch.trim());
  }, [pendingSearch]);

  const handleSort = useCallback((field: string) => {
    setSortDirection(currentDirection => 
      sortField === field 
        ? (currentDirection === 'asc' ? 'desc' : 'asc') 
        : 'desc'
    );
    setSortField(field);
  }, [sortField]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleReportUpdate = () => {
    refetch();
    toast({
      title: 'รายงานถูกอัพเดทแล้ว',
      description: 'ข้อมูลในรายการรายงานได้รับการอัพเดท',
    });
  };

  const setSearchInput = (value: string) => {
    setPendingSearch(value);
  };

  // Persist filters to localStorage
  useEffect(() => {
    saveFilters({
      search,
      statusFilter,
      learningStatusFilter,
      submittedOnTimeFilter,
      hoursDeductedFilter,
      teachingDateFrom: teachingDateFrom?.toISOString() ?? null,
      teachingDateTo: teachingDateTo?.toISOString() ?? null,
      showOtherSubjectsOnly,
    });
  }, [search, statusFilter, learningStatusFilter, submittedOnTimeFilter, hoursDeductedFilter, teachingDateFrom, teachingDateTo, showOtherSubjectsOnly]);

  const resetFilters = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setPendingSearch("");
    setSearch("");
    setStatusFilter("ALL");
    setLearningStatusFilter("ALL");
    setSubmittedOnTimeFilter("ALL");
    setHoursDeductedFilter("ALL");
    setTeachingDateFrom(undefined);
    setTeachingDateTo(undefined);
    setShowOtherSubjectsOnly(false);
    setCurrentPage(1);
  }, []);

  const hasActiveFilters = search.trim() !== "" || statusFilter !== "ALL" || learningStatusFilter !== "ALL" ||
    submittedOnTimeFilter !== "ALL" || hoursDeductedFilter !== "ALL" || !!teachingDateFrom || !!teachingDateTo || showOtherSubjectsOnly;

  return {
    reports,
    isLoading: isLoading || isFetching,
    currentPage,
    totalPages,
    handlePageChange,
    handleReportUpdate,
    refetch,
    search: pendingSearch,
    setSearch: setSearchInput,
    onSearch,
    showOtherSubjectsOnly,
    setShowOtherSubjectsOnly,
    sortField,
    sortDirection,
    handleSort,
    statusFilter,
    setStatusFilter,
    learningStatusFilter,
    setLearningStatusFilter,
    submittedOnTimeFilter,
    setSubmittedOnTimeFilter,
    hoursDeductedFilter,
    setHoursDeductedFilter,
    teachingDateFrom,
    setTeachingDateFrom,
    teachingDateTo,
    setTeachingDateTo,
    resetFilters,
    hasActiveFilters,
  };
};
